/* eslint-disable no-nested-ternary */
import { StaticImage } from 'gatsby-plugin-image'
import React, { lazy, Suspense, useMemo } from 'react'

import { CtaButton, Image, SectionHeaderComponent } from '../../components'
import { Content } from '../../components/Typography'
import { useElementonScreen } from '../../hooks'
import useMediaQuery from '../../hooks/useMediaQuery'
import {
  bacgroundImage,
  bgVideo,
  desktopVideo,
  mobileVideo,
  overlay,
  sectionContainer,
  videoContainer,
} from '../../styles/shared.module.css'
import {
  ALLOWED_WORDS,
  ContentParser,
  replaceWordInString,
} from '../../utils/helpers'
import {
  contentContainer,
  ctaComponentsContainer,
  ctaContainer,
  displayOnDesktop,
  fontColor,
  headerContainerStyling,
  headingClassName,
  headingContainer,
  heroContainer,
  heroHeadingClassName,
  homeContentContainer,
  homeHeaderContainerStyling,
  homeHeroContainer,
  mobileOverLay,
  primaryButton,
  thankYouContainer,
  thankYouHeadingContainer,
} from './HeroContainer.module.css'

const Vimeo = lazy(() => import('@u-wave/react-vimeo'))

const NewHeroContainer = React.memo(
  ({ component, lang, pageType, slice, url }) => {
    const {
      herocontainerwidth,
      herocontentheading,
      herocontentimage,
      herocontentkicker,
      herocontentmobileimage,
      herocontenttext,
    } = slice
    const hasOwnData = slice?.hasowndata || false
    const heroType = useMemo(() => {
      if (hasOwnData) {
        const { herotype } = slice
        return herotype[0]
      }
      return 'FullImage'
    }, [hasOwnData, slice])

    const ctas = useMemo(() => ContentParser(slice, 'herocta'), [slice])
    const isDesktop = useMediaQuery('(min-width: 768px)')
    const [setRef, visible] = useElementonScreen()
    const isThankYouPage = url === '/thank-you/' || url === '/en/thank-you/'
    const isHomePage = pageType === 'homepage'

    const backGroundImage = useMemo(() => {
      if (!isDesktop && component === 'herocontent' && herocontentmobileimage) {
        return herocontentmobileimage
      }
      return herocontentimage
    }, [isDesktop, component, herocontentmobileimage, herocontentimage])

    const isVideo = heroType === 'Video'
    return (
      <section
        ref={setRef}
        className={`${sectionContainer} ${
          isHomePage
            ? homeHeroContainer
            : isThankYouPage
            ? thankYouContainer
            : heroContainer
        }`}
        id={component}
      >
        <div
          className={`${isHomePage ? homeContentContainer : contentContainer}`}
        >
          {!isHomePage ? (
            <SectionHeaderComponent
              component="hero"
              containerwidth={isThankYouPage ? 60 : herocontainerwidth}
              extraClassName={
                isThankYouPage ? thankYouHeadingContainer : headingContainer
              }
              extraContentClassName={fontColor}
              extraHeadingClassName={
                isHomePage ? heroHeadingClassName : headingClassName
              }
              extraHeadingContainerClassName={
                isHomePage ? homeHeaderContainerStyling : headerContainerStyling
              }
              extraKickerClassName={`${fontColor} ${displayOnDesktop}`}
              heroHeader={replaceWordInString(
                herocontentheading,
                'botox',
                ALLOWED_WORDS[lang],
              )}
              kicker={replaceWordInString(
                herocontentkicker,
                'botox',
                ALLOWED_WORDS[lang],
              )}
              renderedContent={replaceWordInString(
                herocontenttext,
                'botox',
                ALLOWED_WORDS[lang],
              )}
            />
          ) : (
            <Content
              extraClassName={heroHeadingClassName}
              renderContent={`<h2>${herocontentheading}</h2>`}
            />
          )}
          <div className={ctaComponentsContainer}>
            {ctas.length > 0 && (
              <div className={ctaContainer}>
                {ctas.map((cta, index) => {
                  let parsedCta = cta
                  if (typeof cta === 'string') {
                    parsedCta = JSON.parse(cta)
                  }
                  return (
                    <CtaButton
                      key={`cta-${cta?.title || index}`}
                      className={primaryButton}
                      {...parsedCta}
                      lang={lang}
                      type="secondary"
                      wide
                    />
                  )
                })}
              </div>
            )}
          </div>
        </div>
        {backGroundImage && visible && !isVideo && (
          <>
            <Image
              alt={backGroundImage.altText || 'Kliniken Image'}
              className={`${bacgroundImage}`}
              loading="eager"
              quality={100}
              src={backGroundImage.node.localFile.childImageSharp}
            />
            <div className={mobileOverLay} />
          </>
        )}
        {isVideo && visible && (
          <Suspense
            fallback={
              <div className={`${bacgroundImage} ${bgVideo}`}>
                <StaticImage
                  alt="fallback"
                  className={`${bacgroundImage}`}
                  loading="eager"
                  priority
                  src="../../assets/images/fallback.avif"
                />
              </div>
            }
          >
            <div className={videoContainer}>
              <div className={`${bacgroundImage} ${bgVideo}`}>
                <Vimeo
                  autoplay
                  background
                  className={mobileVideo}
                  dnt
                  height={1920}
                  loop
                  muted
                  responsive
                  showBadge={false}
                  showByline={false}
                  showPortrait={false}
                  showTitle={false}
                  video="933281713"
                  width={1080}
                />
                <StaticImage
                  alt="fallback"
                  className={`${bacgroundImage}`}
                  loading="eager"
                  priority
                  src="../../assets/images/fallback.avif"
                />
                <Vimeo
                  autoplay
                  background
                  className={desktopVideo}
                  dnt
                  height={1080}
                  loop
                  muted
                  responsive
                  showBadge={false}
                  showByline={false}
                  showPortrait={false}
                  showTitle={false}
                  video="973071961"
                  width={1920}
                />
              </div>
              <div className={overlay} />
            </div>
          </Suspense>
        )}
      </section>
    )
  },
)

export default NewHeroContainer
